import { useNavigate } from "@tanstack/react-router";
import { descripcionCrear, indexDePaso, mapearEstadoAStep, urlPorPaso } from "@/components/solicitudes/stepper/state-mapping";
import { stepsParaTipo, TERMINAL_NEGATIVOS, type PasoSolicitud } from "@/components/solicitudes/stepper/steps-config";
import { Card } from "../Card";
import { EstadoTerminalAlert } from "./EstadoTerminalAlert";
import { VerticalStepper } from "./VerticalStepper";

export type { PasoSolicitud } from "@/components/solicitudes/stepper/steps-config";

export interface SolicitudStepperProps {
  estado: string | null;
  subtipo: string | null;
  tipo?: string | null;
  paso?: PasoSolicitud;
  solicitudId?: string;
  contabilizada?: boolean;
  fillHeight?: boolean;
  motivos?: string[];
  dadaDeBajaPorNombre?: string | null;
  esperaCfdiConciliadoAt?: string | null;
  /** El prorrateo fue automático (CA/OC configurable) · oculta el paso «Prorrateo». */
  prorrateoAutomatico?: boolean;
  /**
   * Si es false, los pasos NO navegan a las rutas de wizard (captura). Se usa
   * para viewers de solo lectura (ej. rol Visor / Mesa de Control) cuyos
   * endpoints de captura exigen `solicitudes.gestionar` (403). Default true (el
   * dueño sí navega para continuar/revisar). */
  permiteNavegar?: boolean;
  /**
   * URL del detalle read-only. Cuando `permiteNavegar` es false pero este href
   * está presente, los pasos accesibles SIGUEN siendo clickeables pero navegan
   * al detalle (donde el resumen muestra datos generales, proveedor, CFDI,
   * prorrateo, documentos, etc.) en lugar de a la ruta de captura. Así un viewer
   * de solo lectura puede consultar el contenido de cada paso sin 403. */
  hrefSoloLectura?: string;
  /**
   * Callback opcional para el modo solo-lectura: al pinchar un paso accesible, en
   * vez de navegar se invoca con la `key` del paso (crear/cfdi/datos/proveedor/
   * prorrateo/documentos/…). Lo usa el detalle para hacer scroll a la sección del
   * resumen. Si se omite, el paso navega a `hrefSoloLectura`. */
  onStepSoloLectura?: (stepKey: string) => void;
}

/**
 * Reemplazo de `components/solicitudes/solicitud-stepper.tsx` (Mantine
 * `<Stepper>`) · misma lógica de resolución de paso activo — se reusa
 * `stepper/steps-config.tsx` y `stepper/state-mapping.ts` TAL CUAL (son
 * lógica pura, sin Mantine) — solo el render cambia, sobre `VerticalStepper`
 * (Etapa 4) en vez del hack CSS de `highlight-styles.ts`.
 */
export function SolicitudStepper({
  estado,
  subtipo,
  tipo,
  paso,
  solicitudId,
  contabilizada = false,
  fillHeight = false,
  motivos,
  dadaDeBajaPorNombre,
  esperaCfdiConciliadoAt = null,
  prorrateoAutomatico = false,
  permiteNavegar = true,
  hrefSoloLectura,
  onStepSoloLectura,
}: SolicitudStepperProps) {
  // Un viewer de solo lectura (no navega a captura) puede aún así abrir el
  // detalle read-only desde cada paso si se le pasó `hrefSoloLectura`.
  const navegaADetalle = !permiteNavegar && !!hrefSoloLectura;
  const navigate = useNavigate();
  const esMultiple = subtipo === "MULTIPLES_CFDI";
  const esContrapago = subtipo === "CONTRAPAGO";
  const esInvoice = subtipo === "INVOICE";
  const esTerminalNegativo = estado !== null && TERMINAL_NEGATIVOS.has(estado);

  if (esTerminalNegativo) {
    return <EstadoTerminalAlert estado={estado!} motivos={motivos} dadaDeBajaPorNombre={dadaDeBajaPorNombre} />;
  }

  const steps = stepsParaTipo(tipo, subtipo, prorrateoAutomatico).map((s) => {
    if (s.key === "crear") return { ...s, description: descripcionCrear(tipo, subtipo) };
    if (s.key === "cfdi" && esMultiple) return { ...s, label: "ZIP de CFDIs", description: "Carga y validación F6" };
    if (s.key === "cfdi" && esInvoice) return { ...s, label: "Factura (PDF)", description: "Sube el PDF · se extrae por OCR" };
    return s;
  });

  const contabIdx = steps.findIndex((s) => s.key === "contabilizacion");

  const activeIndex = paso !== undefined ? indexDePaso(paso, steps) : mapearEstadoAStep(estado, tipo, steps, subtipo, esperaCfdiConciliadoAt);
  const estadoIndex = mapearEstadoAStep(estado, tipo, steps, subtipo, esperaCfdiConciliadoAt);
  const maxAccesibleIndex = Math.max(activeIndex, estadoIndex);

  function esClickable(idx: number): boolean {
    // Bloqueado solo si NO puede navegar a captura, NI a detalle read-only, NI
    // tiene un callback de scroll a secciones (pantalla de autorización).
    if (!permiteNavegar && !navegaADetalle && !onStepSoloLectura) return false;
    if (!solicitudId) return false;
    if (idx === activeIndex) return false;
    if (idx === 0) return false;
    if (idx === contabIdx && contabilizada) return true;
    return idx <= maxAccesibleIndex;
  }

  function handleStepClick(idx: number) {
    if (!solicitudId || !esClickable(idx)) return;
    const key = steps[idx]?.key;
    // Si el consumidor pasó un callback de scroll (pantalla de autorización o
    // detalle read-only), hace scroll a la sección en vez de navegar.
    if (onStepSoloLectura && key) {
      onStepSoloLectura(key);
      return;
    }
    // Viewer de solo lectura sin callback → abre el detalle read-only.
    if (navegaADetalle) {
      void navigate({ to: hrefSoloLectura as never });
      return;
    }
    const url = urlPorPaso(key, solicitudId, esMultiple, esContrapago, esInvoice);
    if (url) void navigate({ to: url as never });
  }

  return (
    <Card padding="md" className={fillHeight ? "h-full overflow-visible" : "overflow-visible"}>
      <VerticalStepper steps={steps} activeIndex={Math.max(0, activeIndex)} maxAccesibleIndex={maxAccesibleIndex} isClickable={esClickable} onStepClick={handleStepClick} />
    </Card>
  );
}
